/**
 * Hawker Commissary Assignment History service
 */
import { Injectable, Injector } from '@angular/core';
import { ColDef, ValueGetterParams } from 'ag-grid-community';

import { BaseService } from '../base';
import {
  IEntityConfig,
  ENTITY_TYPES,
  CELL_RENDERER,
  IXVIListColumn,
  IBaseEntityType,
  GRID_ROW_SELECTION,
} from '@shared/types';
import { get, isEmpty, map, stubFalse } from 'lodash';
import { I18n } from '@app/i18n/strings-keys';
import { EVENT_STATUS_VALUE } from '@shared/types/enums/event-status.enum';
import { isLeaf } from '@app/components/xvi-grid/custom-components/cell-renderer-components/renderer-helpers';
import { BehaviorSubject, Subscription } from '@shared/rx';
import { INTERNAL_GRID_ROW_ID } from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.component.constants';
import { generateId } from '@app/shared/utils/common-utils';
import { formatUserDisplayName } from '@app/shared/utils/user-utils';
import { getListGridRowCellClass } from '@app/shared/helpers/ag-grid-helpers/ag-grid-helpers';
import { XUXListContainerComponent } from '@containers/base';

const MAX_HISTORY_EVENTS = 10;

const entityType: ENTITY_TYPES =
  ENTITY_TYPES.HAWKER_COMMISSARY_ASSIGNMENT_HISTORY;
const config: IEntityConfig = {
  ENTITY_TYPE: entityType,
  isMasterListAvailable: true,
  entityNameToCheckPermissions: ENTITY_TYPES.HAWKER_CONFIG,
  buttons: null,
  apiEndpoint: ENTITY_TYPES.HAWKER_COMMISSARY_ASSIGNMENT,
  uiRoute: entityType,
  list: {
    userGridOptions: {
      enableServerSideFilter: false,
      enableServerSideSorting: false,
      rowSelection: GRID_ROW_SELECTION.SINGLE,
      suppressRowClickSelection: true,
      deltaRowDataMode: false,
      getRowNodeId: (data): string => {
        return get(data, 'event_id', get(data, INTERNAL_GRID_ROW_ID, ''));
      },
      rowClassRules: {
        ...getListGridRowCellClass(),
      },
    },
    gridOptions: {
      multiselect: false,
      treeViewMode: true,
      treeTotalLevel: 0,
      keepNodeOwnExpanded: true,
      keepTreeStructureWhenSave: true,
      treeChildrenPropertyName: 'children',
      disableColumnSelector: true,
      showViewSelector: false,
      showSearch: true,
      showPagination: false,
      hiddenGridHeaderCheckbox: true,
      enableDefaultExpandFn: true,
    },
    isHiddenRefreshButton: true,
    documentOperationOptions: {
      fetchFullListData: false,
    },
    documentQuery: {
      notNeedActive: true,
      filter: `event_status eq '${EVENT_STATUS_VALUE.Closed}'`,
      select: [
        'event_id',
        'event_name',
        'event_date',
        'event_status',
        'commissary_assignments',
      ],
      orderby: 'event_date desc',
      top: MAX_HISTORY_EVENTS,
    },
    listItemsLabel: I18n.Forms.GridMessages.EventsBrackets,
    shouldUpdateNodes: stubFalse,
    warningMessage$: new BehaviorSubject(null),
  },
};

/**
 * Hawker Commissary Assignment History service that extends BaseService to use
 * basic CRUD request operations
 */
@Injectable()
export class HawkerCommissaryAssignmentHistoryService extends BaseService {
  public static config: IEntityConfig = config;
  public static ENTITY_TYPE: string = config.ENTITY_TYPE;
  public xuxListContainer: XUXListContainerComponent;
  public subscriptions: Subscription[] = [];

  public beforeStartListMethods: Function[] = [this.hookXuxListContainer];

  public readonly baseDocumentFilter: string =
    config?.list?.documentQuery?.filter || '';

  public dateRangeField: string = 'event_date';

  public quickFilterFields: string[] = ['event_name'];

  public listProjection: string[] = ['event_name'];

  public headerNames: ColDef[] = [
    {
      headerName: 'Event',
      field: 'event_name',
      filter: false,
    },
  ];

  public constructor(public injector: Injector) {
    super(injector);
    this.config = HawkerCommissaryAssignmentHistoryService.config;

    this.config.list.afterFetchListData = (
      items: IBaseEntityType[],
      options: unknown,
    ) => this.buildListData(items, options);

    this.config.list.columns = <IXVIListColumn[]>[
      {
        path: 'event_name',
        title: I18n.EntityNames.Event,
        cellRenderer: CELL_RENDERER.ag_group_cell_renderer,
        cellRendererParams: {
          noChildrenTrailTips:
            I18n.Forms.HawkerCommissaryAssignmentForm.NoCommissary,
        },
        width: 360,
        showRowGroup: true,
        defaultView: true,
        sortable: true,
        filterParams: {
          quickFilter: true,
        },
      },
      {
        path: 'event_date',
        title: I18n.Common.Date,
        defaultView: true,
        sortable: true,
        width: 140,
        valueFormatter: (params: ValueGetterParams): string => {
          const eventDate = get(params, 'data.event_date', '');

          return isLeaf(params) || isEmpty(eventDate)
            ? ''
            : this.companyService.convertUTCDateWithTimeZone(eventDate, true);
        },
      },
      {
        path: 'site_name',
        title: I18n.EntityNames.Site,
        defaultView: true,
        valueGetter: (params: ValueGetterParams): string => {
          return isLeaf(params) ? params.data.site_name : '';
        },
      },
      {
        path: 'manager_name',
        title: I18n.Forms.HawkerCommissaryAssignmentForm.HawkerManager,
        defaultView: true,
        cellClassRules: {
          'ag-cell-grey-italic-text': (params): boolean => {
            return (
              isLeaf(params) && isEmpty(get(params, 'data.manager_name', ''))
            );
          },
        },
        valueGetter: (params: ValueGetterParams): string => {
          if (!isLeaf(params)) {
            return '';
          }

          const managerName = get(params, 'data.manager_name', '');

          return isEmpty(managerName)
            ? this.i18nService.instant(I18n.Forms.GridForm.NotAssigned)
            : managerName;
        },
      },
    ];
  }

  public buildListData(
    items: IBaseEntityType[],
    options: any = {},
  ): IBaseEntityType[] {
    const data = map(items, (item) => {
      const assignments = get(item, 'commissary_assignments', []);

      const children = map(assignments, (assignment) => {
        const firstName = get(assignment, 'first_name', '');
        const lastName = get(assignment, 'last_name', '');
        const userId = get(assignment, 'user_id', '');
        const rawSiteName = get(assignment, 'site_name', '');
        const siteName = !isEmpty(rawSiteName)
          ? rawSiteName
          : this.i18nService.instant(I18n.Common.Unknown);

        let managerName = '';

        if (!isEmpty(userId)) {
          managerName =
            !isEmpty(firstName) && !isEmpty(lastName)
              ? formatUserDisplayName({
                  first_name: firstName,
                  last_name: lastName,
                })
              : this.i18nService.instant(I18n.Common.Unknown);
        }

        return {
          isLeaf: true,
          site_id: get(assignment, 'site_id', ''),
          site_name: siteName,
          user_id: userId,
          manager_name: managerName,
          [INTERNAL_GRID_ROW_ID]: `group_${generateId()}`,
        };
      });

      return {
        event_id: get(item, 'event_id', ''),
        event_name: get(item, 'event_name', ''),
        event_date: get(item, 'event_date', ''),
        children,
        isLeaf: false,
        [INTERNAL_GRID_ROW_ID]: `group_${generateId()}`,
      };
    });

    const totalItems = get(options, 'originResponse.total');

    if (totalItems > MAX_HISTORY_EVENTS) {
      this.config.list.warningMessage$.next(
        this.i18nService.translate(
          I18n.Forms.HawkerCommissaryAssignmentHistoryForm.ShowingTopEvents,
          { count: MAX_HISTORY_EVENTS, total: totalItems },
        ),
      );
    } else {
      this.config.list.warningMessage$.next('');
    }

    return data;
  }

  public hookXuxListContainer(
    xuxListContainerComponent: XUXListContainerComponent,
  ): void {
    this.xuxListContainer = xuxListContainerComponent;
  }
}
